import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { OAuthService } from 'angular-oauth2-oidc';
import { catchError, defer, exhaustMap, forkJoin, map, Observable, of, tap, timeout } from 'rxjs';
import { SessionService } from '../session.service';
import { AuthActions } from './auth.actions';

/** Cap on loading the discovery document at sign-out before falling back to /signin. */
const DISCOVERY_TIMEOUT_MS = 5000;

export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly oauthService = inject(OAuthService);
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);

  readonly logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        // Close the audit session while the bearer is still usable, and make sure
        // the IdP's end-session URL is known, THEN tear down the OIDC session.
        // Both always complete (bounded by timeouts), so sign-out can't hang.
        exhaustMap(({ endType }) =>
          forkJoin([this.session.end(endType), this.ensureLogoutUrl()]).pipe(
            map(([, canEndIdpSession]) => canEndIdpSession),
          ),
        ),
        tap((canEndIdpSession) => {
          clearStorage(() => localStorage);
          try {
            // Drops the local tokens and, with a logoutUrl, redirects to the IdP's
            // end_session_endpoint, which ends the SSO session and returns the user
            // to POST_LOGOUT_REDIRECT_URL (/signin).
            this.oauthService.logOut();
          } catch {
            canEndIdpSession = false;
          }
          // Navigate ourselves only when the IdP redirect isn't happening: routing
          // to /signin alongside it would start a new code flow whose redirect can
          // override the logout one, re-signing the user in on the live SSO session.
          if (!canEndIdpSession) {
            void this.router.navigate(['/signin']);
          }
        }),
      ),
    { dispatch: false },
  );

  /**
   * The API answered 401 (CSD): the token has expired and, since the API validates it
   * with Curity, so has the Curity session. Clear every locally stored token and the
   * audit session id, then go to /signin, which starts the Curity login. Nothing is
   * sent to the API (it would only 401 again) or to the IdP's end-session endpoint
   * (the session is already gone). Concurrent 401s are handled once.
   */
  readonly sessionExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sessionExpired),
        exhaustMap(() => {
          try {
            // `true`: drop the tokens without redirecting to the IdP's logout.
            this.oauthService.logOut(true);
          } catch {
            // Storage is cleared below regardless.
          }
          // Blocked storage must not stop the redirect to sign-in.
          clearStorage(() => sessionStorage);
          clearStorage(() => localStorage);
          return defer(() => this.router.navigate(['/signin'], { replaceUrl: true })).pipe(
            catchError(() => of(false)),
          );
        }),
      ),
    { dispatch: false },
  );

  /**
   * logOut() only calls the IdP when `logoutUrl` is set, and that comes from the
   * discovery document — which a warm start (page refresh) never loads. Load it
   * on demand; emits whether the IdP logout redirect is possible.
   */
  private ensureLogoutUrl(): Observable<boolean> {
    if (this.oauthService.logoutUrl) {
      return of(true);
    }
    return defer(() => this.oauthService.loadDiscoveryDocument()).pipe(
      timeout(DISCOVERY_TIMEOUT_MS),
      map(() => !!this.oauthService.logoutUrl),
      catchError(() => of(false)),
    );
  }
}

/** Clear a Web Storage area; a blocked or unavailable one (access throws) is skipped. */
function clearStorage(storage: () => Storage): void {
  try {
    storage().clear();
  } catch {
    // Nothing we can clear.
  }
}
